import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import IconButton from "@mui/material/IconButton";
import { FaTimes } from "react-icons/fa";
import { AiOutlinePlusCircle } from "react-icons/ai";
import { RxCross2 } from "react-icons/rx";
import axios from "axios";
import BtnLoader from "Components/Common/Loader/BtnLoader";
import { featchPromoPlan } from "Common Usage/apiService";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 400,
  bgcolor: "background.paper",
  border: "none",
  borderRadius: 8,
  boxShadow: 24,
  p: 4,
};

const CoinEditPopup = ({
  EditCoinModalOpen,
  handleEditModalClose,
  coinDetails,
  fetchCoinPlans,
  staffId,
}) => {
  const [formData, setFormData] = useState({
    title: "",
    promoCodes: [],
    description: "",
    purchaseAmount: "",
    coinAmount: "",
    currency: "INR",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [promoPlans, setPromoPlans] = useState([]);
  const [selectedPromo, setSelectedPromo] = useState("");

  useEffect(() => {
    if (coinDetails) {
      setFormData({
        title: coinDetails.title || "",
        promoCodes: coinDetails.promoCodes || [],
        description: coinDetails.description || "",
        purchaseAmount: coinDetails.purchaseAmount || "",
        coinAmount: coinDetails.coinAmount || "",
        currency: coinDetails.currency || "INR",
      });
    }

    if (EditCoinModalOpen) {
      fetchPromos();
    }
  }, [coinDetails, EditCoinModalOpen]);

  const fetchPromos = async () => {
    try {
      const data = await featchPromoPlan();
      setPromoPlans(data);
    } catch (error) {
      console.error("Error fetching promo codes:", error);
    }
  };

  const handlePromoSelect = (e) => {
    const selected = promoPlans.find((promo) => promo.code === e.target.value);
    setSelectedPromo(selected);
  };

  const addPromoCode = () => {
    if (
      selectedPromo &&
      !formData.promoCodes.some((promo) => promo._id === selectedPromo._id)
    ) {
      setFormData((prev) => ({
        ...prev,
        promoCodes: [...prev.promoCodes, selectedPromo],
      }));
    }
  };

  const removePromoCode = (promoId) => {
    setFormData((prev) => ({
      ...prev,
      promoCodes: prev.promoCodes.filter((promo) => promo._id !== promoId),
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const createStaffActivityLog = async (actionType, description) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/staffs-activity-log/create-staffs-activity`,
        {
          staff: staffId,
          action: actionType,
          description,
        }
      );
    } catch (error) {
      console.error("Activity log failed:", error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/coin-plan/updatecoin-plans/${coinDetails?._id}`,
        formData,
        { headers: { "Content-Type": "application/json" } }
      );

      //  Activity Log Integration
      await createStaffActivityLog(
        "UPDATE_COIN_PLAN",
        `Coin package "${formData.title}" updated (Amount: ₹${
          formData.purchaseAmount
        }, Coins: ${formData.coinAmount}, Promos: ${
          formData.promoCodes.length > 0
            ? formData.promoCodes.length + " applied"
            : "No promo"
        })`
      );

      setSuccess("Coin plan updated successfully!");
      fetchCoinPlans();
      handleEditModalClose();
    } catch (error) {
      setError(
        error.response?.data?.message ||
          "Failed to update coin plan. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal open={EditCoinModalOpen} onClose={handleEditModalClose}>
      <Box sx={style}>
        <IconButton
          onClick={handleEditModalClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "#c51369" }}
        >
          <FaTimes />
        </IconButton>
        <h2 className="mb-6 mt-4 text-center text-2xl font-bold text-navy-700">
          Edit Coins Package
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="mb-2 block font-bold text-gray-700">
                Title
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="w-full appearance-none rounded border px-3 py-2 text-gray-700 shadow focus:outline-none"
              />
            </div>

            <div>
              <label className="mb-2 block font-bold text-gray-700">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="w-full appearance-none rounded border px-3 py-2 text-gray-700 shadow focus:outline-none"
              />
            </div>

            {/* Promo Code Selection */}
            <div className="flex gap-2">
              <select
                onChange={handlePromoSelect}
                className="w-full rounded border p-2"
              >
                <option value="">Select a Promo Code</option>
                {promoPlans.map((promo) => (
                  <option key={promo._id} value={promo.code}>
                    {promo.code} ({promo.discount}%)
                  </option>
                ))}
              </select>
              <button type="button" onClick={addPromoCode}>
                <AiOutlinePlusCircle size={30} />
              </button>
            </div>

            {/* Display Selected Promo Codes */}
            <div className="flex flex-wrap gap-2">
              {formData.promoCodes.map((promo) => (
                <div
                  key={promo._id}
                  className="flex gap-2 rounded-2xl bg-[#61c5e6] px-2 py-1"
                >
                  <span>{promo.code}</span>
                  <button
                    type="button"
                    onClick={() => removePromoCode(promo._id)}
                  >
                    <RxCross2 size={20} />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex gap-4">
              <div>
                <label className="mb-2 block font-bold text-gray-700">
                  Purchase Amount
                </label>
                <input
                  type="number"
                  name="purchaseAmount"
                  value={formData.purchaseAmount}
                  onChange={handleChange}
                  className="w-full appearance-none rounded border px-3 py-2 text-gray-700 shadow focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-2 block font-bold text-gray-700">
                  Coin Amount
                </label>
                <input
                  type="number"
                  name="coinAmount"
                  value={formData.coinAmount}
                  onChange={handleChange}
                  className="w-full appearance-none rounded border px-3 py-2 text-gray-700 shadow focus:outline-none"
                />
              </div>
            </div>
          </div>

          <div className="mt-6">
            {isLoading ? (
              <BtnLoader />
            ) : (
              <button
                type="submit"
                className="w-full rounded bg-[#c51369] p-3 text-white"
              >
                Update Package
              </button>
            )}
          </div>

          {error && <p className="mt-2 text-red-500">{error}</p>}
          {success && <p className="mt-2 text-green-500">{success}</p>}
        </form>
      </Box>
    </Modal>
  );
};

export default CoinEditPopup;
